import { Eye, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  DataTable,
  SortableHeadCell,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableHeadCell,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { FilterBar } from "@/components/filter-bar";
import { Highlight } from "@/components/highlight";
import { RowActions } from "@/components/row-action";
import { SearchMultiSelect } from "@/components/search-multi-select";
import type { StartViewAs, ViewAsPerson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { fullName } from "@/lib/navigation";
import { isAdminRole, roleLabel, ROLES } from "@/lib/roles";
import { sortRows, useTableSort } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import {
  filterViewAsPeople,
  hasViewAsFilters,
  NO_VIEW_AS_FILTERS,
  VIEW_AS_SORT_COLUMNS,
  VIEW_AS_SORT_INITIAL,
  type ViewAsFilters,
  viewAsSortValue,
} from "./utils";

/** Name, role, teams, last active, the action. */
const COLUMNS = 5;

interface ViewAsPeopleTableProps {
  /** Everyone the developer can view the dashboard as. */
  people: readonly ViewAsPerson[];
  /** Starts viewing the dashboard as the person. */
  onView: (target: StartViewAs) => void;
}

/**
 * Everyone the developer can view the dashboard as, in one dense table in
 * the Tools page's style, as the Mensen tab lists them: the name with the
 * email under it (the email alone without a name), the role as the Mensen
 * tab's chip, the teams as chips ("Iedereen zonder team" for none), when
 * they last used DC Data, and "Bekijken als". A search on name and email
 * and a role filter sit above it; every column sorts, by name at first. In
 * the phone layout the role, the teams and the last activity move under the
 * name, and the action into a menu.
 */
export function ViewAsPeopleTable({ people, onView }: ViewAsPeopleTableProps) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ViewAsFilters>(NO_VIEW_AS_FILTERS);
  const sort = useTableSort({ columns: VIEW_AS_SORT_COLUMNS, initial: VIEW_AS_SORT_INITIAL });
  const shown = sortRows(filterViewAsPeople(people, filters), sort.sort, viewAsSortValue);
  const filtered = hasViewAsFilters(filters);
  const reset = () => setFilters(NO_VIEW_AS_FILTERS);

  return (
    <div className="space-y-3">
      <FilterBar
        search={{
          value: filters.search,
          onChange: (search) => setFilters({ ...filters, search }),
          label: t("viewAs.search"),
        }}
        columns="medium"
        filtered={filtered}
        onReset={reset}
      >
        <SearchMultiSelect
          options={ROLES.map((role) => ({ value: role, label: roleLabel(role) }))}
          value={filters.roles}
          onChange={(roles) => setFilters({ ...filters, roles })}
          name={t("viewAs.column.role")}
          allLabel={t("viewAs.allRoles")}
          countLabel={(count) => t("viewAs.roleCount", { count })}
          searchLabel={t("viewAs.searchRole")}
          className="w-full"
        />
      </FilterBar>
      <DataTable label={t("viewAs.people")}>
        <TableHead>
          <SortableHeadCell {...sort.head("name")} className="md:min-w-52">
            {t("viewAs.column.name")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("role")} narrow="hide">
            {t("viewAs.column.role")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("teams")} narrow="hide">
            {t("viewAs.column.teams")}
          </SortableHeadCell>
          <SortableHeadCell {...sort.head("last")} narrow="hide">
            {t("viewAs.column.lastActive")}
          </SortableHeadCell>
          <TableHeadCell align="right" className="w-px">
            <span className="sr-only">{t("viewAs.column.actions")}</span>
          </TableHeadCell>
        </TableHead>
        <tbody>
          {shown.map((person) => (
            <PersonRow key={person.id} person={person} search={filters.search} onView={onView} />
          ))}
          {shown.length === 0 && (
            <TableEmptyRow colSpan={COLUMNS} onReset={filtered ? reset : undefined}>
              {people.length === 0 ? t("viewAs.empty") : t("viewAs.noMatch")}
            </TableEmptyRow>
          )}
        </tbody>
      </DataTable>
    </div>
  );
}

interface PersonRowProps {
  person: ViewAsPerson;
  /** The search as typed, marked in the name and the email. */
  search: string;
  onView: (target: StartViewAs) => void;
}

function PersonRow({ person, search, onView }: PersonRowProps) {
  const { t } = useTranslation();
  const name = fullName(person);
  const label = name ?? person.email;
  const lastActive = person.last_active_at ? formatDateTime(person.last_active_at) : null;
  const role = (
    <Chip tone={isAdminRole(person.role) ? "read" : "neutral"}>{roleLabel(person.role)}</Chip>
  );
  const teams =
    person.teams.length === 0 ? (
      <span className="text-muted text-xs">{t("teams.defaultTeam")}</span>
    ) : (
      person.teams.map((team) => <Chip key={team}>{team}</Chip>)
    );

  return (
    <TableRow className="align-top">
      <TableCell>
        <TableItem
          icon={<UserRound aria-hidden="true" strokeWidth={1.75} className={TABLE_ICON_CLASS} />}
          title={
            <span className={name ? "break-words" : "break-all"}>
              <Highlight text={label} query={search} />
            </span>
          }
          summary={
            name && (
              <span className="break-all">
                <Highlight text={person.email} query={search} />
              </span>
            )
          }
          facts={
            // The row reads top to bottom: the role and the teams, then the last activity.
            <>
              <span className="flex flex-wrap items-center gap-1">
                {role}
                {teams}
              </span>
              <TableFacts>
                {lastActive
                  ? t("viewAs.lastActiveFact", { time: lastActive })
                  : t("viewAs.noCalls")}
              </TableFacts>
            </>
          }
        />
      </TableCell>
      <TableCell narrow="hide">{role}</TableCell>
      <TableCell narrow="hide">
        <span className="flex flex-wrap gap-1">{teams}</span>
      </TableCell>
      <TableCell
        narrow="hide"
        className={cn("text-xs whitespace-nowrap tabular-nums", !lastActive && "text-muted")}
      >
        {lastActive ?? t("viewAs.noCalls")}
      </TableCell>
      <TableCell align="right" className="px-1 py-1">
        <RowActions
          label={t("common.actionsFor", { name: label })}
          actions={[
            {
              label: t("viewAs.actionFor", { name: label }),
              short: t("viewAs.action"),
              icon: Eye,
              onClick: () => onView({ person_id: person.id }),
            },
          ]}
        />
      </TableCell>
    </TableRow>
  );
}
